import type { QueueBoardRoom } from "../types";
import { queueFont } from "../queueFont";
import { CareCornerLogo } from "./CareCornerLogo";
import { useSideVideoLayout } from "./SideVideoLayoutContext";
import { WelcomeHeader } from "./WelcomeHeader";

type RoomCardsGridProps = {
  clinicName: string;
  rooms: QueueBoardRoom[];
};

function stackedCardScale(roomCount: number) {
  if (roomCount <= 2) {
    return {
      name: "text-[clamp(18px,2.8vmin,26px)]",
      doctor: "text-[clamp(14px,2vmin,18px)]",
      label: "text-[clamp(11px,1.4vmin,13px)]",
      queue: "text-[clamp(72px,16vmin,140px)]",
      gap: "gap-[clamp(10px,2vmin,18px)]",
      pad: "px-[clamp(12px,2.5vmin,24px)] py-[clamp(12px,2vmin,20px)]",
    };
  }
  if (roomCount <= 4) {
    return {
      name: "text-[clamp(15px,2.2vmin,20px)]",
      doctor: "text-[clamp(12px,1.6vmin,15px)]",
      label: "text-[10px]",
      queue: "text-[clamp(52px,11vmin,96px)]",
      gap: "gap-[clamp(6px,1.2vmin,12px)]",
      pad: "px-3 py-2",
    };
  }
  return {
    name: "text-[clamp(13px,1.8vmin,16px)]",
    doctor: "text-[clamp(11px,1.4vmin,13px)]",
    label: "text-[9px]",
    queue: "text-[clamp(36px,8vmin,64px)]",
    gap: "gap-1",
    pad: "px-2 py-1.5",
  };
}

function RoomCard({
  room,
  stacked,
  roomCount,
}: {
  room: QueueBoardRoom;
  stacked?: boolean;
  roomCount?: number;
}) {
  if (stacked && roomCount) {
    const scale = stackedCardScale(roomCount);
    return (
      <div
        className={[
          queueFont,
          "flex min-h-0 flex-1 flex-col items-center justify-center rounded-xl border border-solid border-[#cbd5e1] bg-gradient-to-b from-slate-50 to-white text-center shadow-[0_4px_16px_rgba(15,23,42,0.06)]",
          scale.pad,
        ].join(" ")}
      >
        <div className={["flex max-w-full min-h-0 flex-col items-center", scale.gap].join(" ")}>
          <div
            className={`${queueFont} w-full break-words font-extrabold leading-snug text-[#0b3a66] [overflow-wrap:anywhere] ${scale.name}`}
          >
            {room.name}
          </div>
          <div
            className={`${queueFont} w-full break-words font-semibold leading-snug text-[#334155] [overflow-wrap:anywhere] ${scale.doctor}`}
          >
            {room.doctorName}
          </div>
          <div
            className={`font-bold uppercase leading-tight tracking-[0.12em] text-slate-500 ${scale.label}`}
          >
            Now serving
          </div>
          <div
            className={`${queueFont} w-full font-extrabold leading-none text-[#0b3a66] [font-variant-numeric:tabular-nums] ${scale.queue}`}
          >
            {room.currentQueue}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div
      className={`${queueFont} flex min-h-[clamp(320px,46vmin,560px)] min-w-0 flex-col items-center justify-center rounded-[clamp(12px,1.5vmin,20px)] border-[clamp(1px,0.15vmin,2px)] border-solid border-[#cbd5e1] bg-gradient-to-b from-slate-50 to-white px-[clamp(20px,3.4vmin,40px)] py-[clamp(28px,4.5vmin,52px)] text-center shadow-[0_8px_28px_rgba(15,23,42,0.07)]`}
    >
      <div className="flex max-w-full flex-col items-center gap-[14px]">
        <div
          className={`${queueFont} w-full break-words text-[22px] font-extrabold leading-snug text-[#0b3a66] [overflow-wrap:anywhere] [text-rendering:optimizeLegibility]`}
        >
          {room.name}
        </div>
        <div
          className={`${queueFont} w-full break-words text-lg font-semibold leading-snug text-[#334155] [overflow-wrap:anywhere] [text-rendering:optimizeLegibility]`}
        >
          {room.doctorName}
        </div>
        <div className="text-[13px] font-bold uppercase leading-tight tracking-[0.14em] text-slate-500">
          Now serving
        </div>
        <div
          className={`${queueFont} w-full text-[160px] font-extrabold leading-none text-[#0b3a66] [font-variant-numeric:tabular-nums] [text-rendering:optimizeLegibility]`}
        >
          {room.currentQueue}
        </div>
      </div>
    </div>
  );
}

export function RoomCardsGrid({ clinicName, rooms }: RoomCardsGridProps) {
  const sideVideo = useSideVideoLayout();
  const multiRoomColumnStack = sideVideo && rooms.length > 1;

  return (
    <div
      className={[
        queueFont,
        "relative box-border flex w-full max-w-[100vw] select-none flex-col overflow-hidden bg-white antialiased",
        multiRoomColumnStack ? "h-full min-h-0 flex-1" : "min-h-screen overflow-x-hidden",
      ].join(" ")}
    >
      {!multiRoomColumnStack ? <CareCornerLogo variant="grid" /> : null}

      <WelcomeHeader clinicName={clinicName} layout="inline" compact={multiRoomColumnStack} />

      <div
        className={[
          "flex min-h-0 w-full flex-1 flex-col items-stretch",
          multiRoomColumnStack
            ? "overflow-hidden px-3 pb-3 pt-1"
            : "justify-center overflow-x-hidden overflow-y-auto px-[clamp(20px,3.5vmin,64px)] pb-[clamp(96px,11vmin,160px)]",
        ].join(" ")}
      >
        {rooms.length === 0 ? (
          <div
            className={`${queueFont} max-w-[520px] self-center p-[clamp(24px,4vmin,48px)] text-center text-lg leading-normal text-[#334155]`}
          >
            No rooms are configured for this clinic yet. Add rooms in Settings (Manage time → Rooms), then
            refresh this page.
          </div>
        ) : multiRoomColumnStack ? (
          <div className="flex h-full min-h-0 w-full flex-1 flex-col gap-2">
            {rooms.map((room) => (
              <RoomCard key={room.id} room={room} stacked roomCount={rooms.length} />
            ))}
          </div>
        ) : (
          <div className="mx-auto grid w-full max-w-[min(1800px,96vw)] grid-cols-[repeat(auto-fit,minmax(min(240px,100%),1fr))] gap-[clamp(16px,2.2vmin,32px)] self-center">
            {rooms.map((room) => (
              <RoomCard key={room.id} room={room} />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
